import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import {
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

const ViewSRR = () => {
  const [filters, setFilters] = useState({
    srr_code: '',
    heat_no: '',
    mill_code: '',
    steel_code: '',
    from_date: '',
    to_date: '',
    status: ''
  });

  // Dialog states
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editForm, setEditForm] = useState({});

  const { enqueueSnackbar } = useSnackbar();
  const { api } = useAuth();

  const { data: srrData, isLoading, error, refetch } = useQuery(
    ['viewSRR', filters],
    async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/raw-material/srr?${params}`);
      return response.data;
    },
    {
      onError: (error) => {
        enqueueSnackbar(`Error fetching SRR data: ${error.message}`, { variant: 'error' });
      }
    }
  );

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    enqueueSnackbar('Download functionality will be implemented', { variant: 'info' });
  };

  const handleView = (record) => {
    setSelectedRecord(record);
    setOpenViewDialog(true);
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setEditForm({
      srr_code: record.srr_code || '',
      srr_no: record.srr_no || '',
      srr_date: record.srr_date || '',
      mill_code: record.mill_code || '',
      steel_code: record.steel_code || '',
      heat_no: record.heat_no || '',
      received_bars: record.received_bars || '',
      received_quantity: record.received_quantity || '',
      accepted_bars: record.accepted_bars || '',
      accepted_quantity: record.accepted_quantity || '',
      rejected_bars: record.rejected_bars || '',
      rejected_quantity: record.rejected_quantity || '',
      location: record.location || '',
      status: record.status || '',
      remarks: record.remarks || ''
    });
    setOpenEditDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenViewDialog(false);
    setOpenEditDialog(false);
    setSelectedRecord(null);
    setEditForm({});
  };

  const handleSave = async () => {
    try {
      const data = { ...editForm };
      const numericFields = ['received_bars', 'received_quantity', 'accepted_bars', 'accepted_quantity', 'rejected_bars', 'rejected_quantity'];
      numericFields.forEach(field => {
        if (data[field] === '' || data[field] === undefined) data[field] = null;
      });
      await api.put(`/raw-material/srr/${selectedRecord.srr_id}`, data);
      enqueueSnackbar('SRR updated successfully', { variant: 'success' });
      handleCloseDialogs();
      refetch(); // Refresh the data
    } catch (error) {
      enqueueSnackbar(`Error updating SRR: ${error.message}`, { variant: 'error' });
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'received': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading SRR data: {error.message}
        </Alert>
      </Box>
    );
  }

  const srrRecords = srrData?.data?.srr || [];

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        View Steel Receipt Reports (SRR)
      </Typography>

      {/* Search Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="SRR Code"
              value={filters.srr_code}
              onChange={(e) => setFilters({ ...filters, srr_code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Heat No"
              value={filters.heat_no}
              onChange={(e) => setFilters({ ...filters, heat_no: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Mill Code"
              value={filters.mill_code}
              onChange={(e) => setFilters({ ...filters, mill_code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Steel Code"
              value={filters.steel_code}
              onChange={(e) => setFilters({ ...filters, steel_code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={() => refetch()}
              startIcon={<SearchIcon />}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box mb={3} display="flex" gap={2}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Export to Excel
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="primary">
                    {srrRecords.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Records
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="success.main">
                    {srrRecords.filter(r => r.status === 'received').length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Received
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="warning.main">
                    {srrRecords.filter(r => r.status === 'pending').length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Pending
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="error.main">
                    {srrRecords.reduce((sum, r) => sum + (r.rejected_quantity || 0), 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Rejected
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* SRR Data Table */}
          <Paper>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                Steel Receipt Reports
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {srrRecords.length} records found
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>SRR Code</TableCell>
                    <TableCell>SRR No</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Mill Code</TableCell>
                    <TableCell>Steel Code</TableCell>
                    <TableCell>Heat No</TableCell>
                    <TableCell>Received Qty</TableCell>
                    <TableCell>Accepted Qty</TableCell>
                    <TableCell>Rejected Qty</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {srrRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        No SRR records found for the selected criteria
                      </TableCell>
                    </TableRow>
                  ) : (
                    srrRecords.map((record, index) => (
                      <TableRow key={index}>
                        <TableCell>{record.srr_code}</TableCell>
                        <TableCell>{record.srr_no}</TableCell>
                        <TableCell>{record.srr_date}</TableCell>
                        <TableCell>{record.mill_code}</TableCell>
                        <TableCell>{record.steel_code}</TableCell>
                        <TableCell>{record.heat_no}</TableCell>
                        <TableCell>{record.received_quantity}</TableCell>
                        <TableCell>{record.accepted_quantity}</TableCell>
                        <TableCell>{record.rejected_quantity}</TableCell>
                        <TableCell>
                          <Chip 
                            label={record.status || 'N/A'} 
                            color={getStatusColor(record.status)} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleView(record)}
                          >
                            <ViewIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="secondary"
                            onClick={() => handleEdit(record)}
                          >
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={handleCloseDialogs} maxWidth="md" fullWidth>
        <DialogTitle>Steel Receipt Report Details</DialogTitle>
        <DialogContent>
          {selectedRecord && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">SRR Code</Typography>
                <Typography variant="body1">{selectedRecord.srr_code || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">SRR Number</Typography>
                <Typography variant="body1">{selectedRecord.srr_no || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Date</Typography>
                <Typography variant="body1">{selectedRecord.srr_date || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Mill Code</Typography>
                <Typography variant="body1">{selectedRecord.mill_code || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Steel Code</Typography>
                <Typography variant="body1">{selectedRecord.steel_code || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Heat Number</Typography>
                <Typography variant="body1">{selectedRecord.heat_no || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Quantities</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary">Received</Typography>
                <Typography variant="body1">{selectedRecord.received_quantity || '0'} ({selectedRecord.received_bars || '0'} bars)</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary">Accepted</Typography>
                <Typography variant="body1">{selectedRecord.accepted_quantity || '0'} ({selectedRecord.accepted_bars || '0'} bars)</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary">Rejected</Typography>
                <Typography variant="body1">{selectedRecord.rejected_quantity || '0'} ({selectedRecord.rejected_bars || '0'} bars)</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Location</Typography>
                <Typography variant="body1">{selectedRecord.location || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={selectedRecord.status || 'N/A'}
                    color={getStatusColor(selectedRecord.status)}
                    size="small"
                  />
                </Box>
              </Grid>
              {selectedRecord.remarks && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Remarks</Typography>
                  <Typography variant="body1">{selectedRecord.remarks}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Close</Button>
          <Button 
            onClick={() => {
              handleCloseDialogs();
              handleEdit(selectedRecord);
            }} 
            variant="contained"
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseDialogs} maxWidth="lg" fullWidth>
        <DialogTitle>Edit Steel Receipt Report</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SRR Code"
                value={editForm.srr_code || ''}
                onChange={(e) => setEditForm({...editForm, srr_code: e.target.value})}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SRR Number"
                value={editForm.srr_no || ''}
                onChange={(e) => setEditForm({...editForm, srr_no: e.target.value})}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={editForm.srr_date || ''}
                onChange={(e) => setEditForm({...editForm, srr_date: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mill Code"
                value={editForm.mill_code || ''}
                onChange={(e) => setEditForm({...editForm, mill_code: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Steel Code"
                value={editForm.steel_code || ''}
                onChange={(e) => setEditForm({...editForm, steel_code: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Heat Number"
                value={editForm.heat_no || ''}
                onChange={(e) => setEditForm({...editForm, heat_no: e.target.value})}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Quantities</Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Received Bars"
                type="number"
                value={editForm.received_bars || ''}
                onChange={(e) => setEditForm({...editForm, received_bars: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Received Quantity"
                type="number"
                step="0.001"
                value={editForm.received_quantity || ''}
                onChange={(e) => setEditForm({...editForm, received_quantity: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Accepted Bars"
                type="number"
                value={editForm.accepted_bars || ''}
                onChange={(e) => setEditForm({...editForm, accepted_bars: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Accepted Quantity"
                type="number"
                step="0.001"
                value={editForm.accepted_quantity || ''}
                onChange={(e) => setEditForm({...editForm, accepted_quantity: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rejected Bars"
                type="number"
                value={editForm.rejected_bars || ''}
                onChange={(e) => setEditForm({...editForm, rejected_bars: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rejected Quantity"
                type="number"
                step="0.001"
                value={editForm.rejected_quantity || ''}
                onChange={(e) => setEditForm({...editForm, rejected_quantity: e.target.value})}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={editForm.location || ''}
                onChange={(e) => setEditForm({...editForm, location: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editForm.status || ''}
                  label="Status"
                  onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                >
                  <MenuItem value="received">Received</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="accepted">Accepted</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                multiline
                rows={3}
                value={editForm.remarks || ''}
                onChange={(e) => setEditForm({...editForm, remarks: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViewSRR;